import * as Sentry from '@sentry/nextjs';
import {
  PrecomputedEvaluationsInterface,
  useStatsigClient,
} from '@statsig/react-bindings';
import { useCallback } from 'react';

import { useMarket } from '../providers/MarketProvider';
import { useSembonusUser } from '../providers/UserProvider';
import { AffiliateService } from '../services/AffiliateService/AffiliateService';
import {
  flattenPropertiesAsStrings,
  isSSR,
  propertiesAsStrings,
  removeNestedPropertyFromObject,
} from '../utils/helpers';
import { getUnlocalizedUrl } from '../utils/markets';
import { AllowedEventNames } from '../utils/types';
import { isCrawlerUserAgent } from './useIsCrawler/useIsCrawler.utils';

export const useLogService = () => {
  const statsigClient = useStatsigClient();
  const user = useSembonusUser();
  const market = useMarket();
  const userAgent = (!isSSR() && navigator.userAgent) || '';
  const isCrawler = isCrawlerUserAgent(userAgent);

  const enrichMetadata = useCallback(
    (
      inputMetadata?:
        | Record<string, string | null | undefined>
        | null
        | undefined
    ) => {
      let metadata = {
        ...(!isSSR()
          ? {
              origin: window.location.origin,
              pathname: window.location.pathname,
              search: window.location.search,
              href: window.location.href,
              hash: window.location.hash,
              host: window.location.host,
              hostname: window.location.hostname,
              referrer: document.referrer,
              isCrawler: `${isCrawler}`,
            }
          : {}),
        ...propertiesAsStrings(market),
        ...flattenPropertiesAsStrings({
          user: removeNestedPropertyFromObject(user, 'memberOptions'),
        }),
        ...propertiesAsStrings(inputMetadata),
      };

      const unlocalized =
        market && !isSSR()
          ? getUnlocalizedUrl(market, window.location.pathname)
          : undefined;

      if (unlocalized) {
        metadata = {
          ...metadata,
          ...flattenPropertiesAsStrings({ unlocalized }),
        };
      }

      const affiliate = { affiliateId: AffiliateService.getAffiliateId() };
      if (affiliate.affiliateId) {
        metadata = {
          ...metadata,
          ...flattenPropertiesAsStrings({ affiliate }),
        };
      }

      return metadata;
    },
    [market, user]
  );

  const logEvent = (
    event: AllowedEventNames,
    value?: Parameters<PrecomputedEvaluationsInterface['logEvent']>[1],
    inputMetadata?: Record<string, string | null | undefined> | null | undefined
  ) => {
    const metadata = enrichMetadata(inputMetadata);

    if (!isCrawler) {
      statsigClient.logEvent(event, value, metadata);
    }

    // just to enforce we don't accidentally change the event properties
    // that need to be in the dataLayer
    (
      window.dataLayer as Array<{
        event: string;
        metadata: Record<string, unknown>;
      }>
    )?.push({ event, metadata });
  };

  const logErrorWithMetadata = (
    inputMetadata: Record<string, string | null | undefined> | null | undefined,
    ...args: Parameters<typeof Sentry.captureException>
  ) => {
    const metadata = enrichMetadata(inputMetadata);

    return Sentry.withScope((scope) => {
      scope.setTags(metadata);

      return Sentry.captureException(...args);
    });
  };

  return {
    logEvent,
    logError: (...args: Parameters<typeof Sentry.captureException>) =>
      logErrorWithMetadata(null, ...args),
    logErrorWithMetadata,
  };
};
